import { roles } from 'common-src/models/Client/constants';
import { InteractionReason, InteractionType } from 'common-src/models/PatientInteraction/constants';
import { moduleTypes } from 'common-src/models/PatientInteraction/protocolModules';

export const defaultPermissions = Object.freeze({
  canEditPlatformSettings: false,
  canCreateClient: false,
  canManageClients: false,
  canRequestReports: false,
  canViewChartReviews: false,
  canRequestGuideReports: false,
  canRequestEligibilityReports: false,
  canCreateBulkPortalAccess: false,
  canImportXifinCoverage: false,
  isVisibleInApp: false,
  canManagePromptTemplates: false,
  canManageScheduledMessages: false,
  canManageVisits: false,
  canCreatePatient: false,
  canManageLeads: false,
  patientChart: {
    quickSettings: {},
    actions: {
      canAddLabResult: false,
      canCloseAccount: false,
      canAddTask: false,
      canAddDocumentation: false,
      canPlaceAccountOnHold: false,
      canChangeStatusToActive: false,
      canRpmApprove: false,
      canAddSmartGoal: false,
      canAddPrescription: false,
      canReopenAccount: false,
      canRequestMedicationUpdates: false,
      canSendDocumentation: false,
      canSendBulkDocumentation: false,
      canSendLmn: false,
      canAddMedication: false,
      canAddNewIntake: false,
      canMfaApprove: false,
      canClearBGMAlertStatus: false,
      canClearBGMAlerts: false,
      canSendReferral: false,
      canSelectReferredBy: false,
      cognitive: false,
      canSwitchCGMToBGM: false,
    },
    sections: {
      memberInformation: false,
      contacts: false,
      glucoseReadings: false,
      vitalsAndLabResults: {
        editLabResult: false,
        deleteLabResult: false,
      },
      smartGoals: false,
      conversationHistory: false,
      physicianOrders: {
        setRiskLevel: false,
        setAssignedProviders: false,
        assignedAsAC: false,
        assignedAsRD: false,
        assignedAsCDCES: false,
        assignedAsLcsw: false,
        setDiagnosis: false,
        setInsulinUse: false,
        setCGMUse: false,
        setInsulinPump: false,
        setStripOrders: false,
        setTestingOrders: false,
        addAdditionalTestingInfo: false,
      },
      orders: {
        alerts: false,
        labOrders: false,
      },
      medicationsList: false,
      allergyList: false,
      medicalHistory: false,
      familyHistory: false,
      socialHistory: false,
      supplementsList: false,
      encountersList: false,
      surgicalHistory: false,
      scheduledCalls: {
        canEditScheduledCall: false,
        canEditVisitStatus: false,
        canEditVisitAssigned: false,
      },
      careProviders: false,
      documentation: {
        addendAnyNote: false,
        addDocumentation: false,
        requestAdjustments: false,
        downloadDocument: false,
      },
      devicesAndOrders: {
        addBGMDevice: false,
        placeOrder: false,
        setStripCount: false,
        addCGMDevice: false,
        markAsCanceled: false,
        markAsFailed: false,
        deleteBGMDevice: false,
      },
      knownPlans: {
        editBeneficiaryInfo: false,
        addPlan: false,
        editPlan: false,
        changePlanStatus: false,
      },
      payorPriority: {
        editHierarchy: false,
      },
      preAuths: {
        addAuth: false,
        editAuth: false,
      },
      exemptions: {
        addExemption: false,
        editExemption: false,
      },
      cgmOrders: {
        addCGMDevice: false,
        addCGMOrder: false,
        viewCGMOrder: false,
      },
      team: false,
      sdohList: {
        addSdoh: false,
        editSdoh: false,
      },
      tasks: {
        editTask: false,
        addTask: false,
      },
      cognitive: false,
      documents: {
        deleteDocument: false,
      },
      caregivers: false,
      scores: false,
      alignmentDownload: false,
      alignmentData: false,
      athena: false,
      secureMessaging: false,
    },
  },
  documentation: {
    enrollment: {},
    clinical: {},
    other: {},
    questionnaire: {},
    caseWork: {},
    cognitive: {},
    guide: {},
  },
  note: {},
});

export const adminPermissions = Object.freeze({
  canEditPlatformSettings: true,
  canCreateClient: true,
  canManageClients: true,
  canRequestReports: true,
  canViewChartReviews: true,
  canRequestGuideReports: true,
  canRequestEligibilityReports: true,
  canCreateBulkPortalAccess: true,
  canImportXifinCoverage: true,
  isVisibleInApp: true,
  canManagePromptTemplates: true,
  canManageScheduledMessages: true,
  canManageVisits: true,
  canCreatePatient: true,
  canManageLeads: true,
  patientChart: {
    quickSettings: {
      canSetAvailableForAlerts: true,
    },
    actions: {
      canAddLabResult: true,
      canCloseAccount: true,
      canAddTask: true,
      canAddDocumentation: true,
      canPlaceAccountOnHold: true,
      canChangeStatusToActive: true,
      canRpmApprove: true,
      canAddSmartGoal: true,
      canAddPrescription: true,
      canReopenAccount: true,
      canRequestMedicationUpdates: true,
      canSendDocumentation: true,
      canSendBulkDocumentation: true,
      canSendLmn: true,
      canAddMedication: true,
      canAddNewIntake: true,
      canMfaApprove: true,
      canClearBGMAlertStatus: true,
      canClearBGMAlerts: true,
      canSendReferral: true,
      canSelectReferredBy: true,
      cognitive: true,
      canSwitchCGMToBGM: true,
    },
    sections: {
      memberInformation: true,
      contacts: true,
      glucoseReadings: true,
      vitalsAndLabResults: {
        editLabResult: true,
        deleteLabResult: true,
      },
      smartGoals: true,
      conversationHistory: true,
      physicianOrders: {
        setRiskLevel: true,
        setAssignedProviders: true,
        assignedAsAC: false,
        assignedAsRD: false,
        assignedAsCDCES: false,
        assignedAsLcsw: false,
        setDiagnosis: true,
        setInsulinUse: true,
        setCGMUse: true,
        setInsulinPump: true,
        setStripOrders: true,
        setTestingOrders: true,
        addAdditionalTestingInfo: true,
      },
      orders: {
        alerts: true,
        labOrders: true,
      },
      medicationsList: true,
      allergyList: true,
      medicalHistory: true,
      familyHistory: true,
      socialHistory: true,
      supplementsList: true,
      encountersList: true,
      surgicalHistory: true,
      scheduledCalls: {
        canEditScheduledCall: true,
        canEditVisitStatus: true,
        canEditVisitAssigned: true,
      },
      careProviders: true,
      documentation: {
        addendAnyNote: true,
        addDocumentation: true,
        requestAdjustments: true,
        downloadDocument: true,
      },
      devicesAndOrders: {
        addBGMDevice: true,
        placeOrder: true,
        setStripCount: true,
        addCGMDevice: true,
        markAsCanceled: true,
        markAsFailed: true,
        deleteBGMDevice: true,
      },
      knownPlans: {
        editBeneficiaryInfo: true,
        addPlan: true,
        editPlan: true,
        changePlanStatus: true,
      },
      payorPriority: {
        editHierarchy: true,
      },
      preAuths: {
        addAuth: true,
        editAuth: true,
      },
      exemptions: {
        addExemption: true,
        editExemption: true,
      },
      cgmOrders: {
        addCGMDevice: true,
        addCGMOrder: true,
        viewCGMOrder: true,
      },
      team: true,
      sdohList: {
        addSdoh: true,
        editSdoh: true,
      },
      tasks: {
        editTask: true,
        addTask: true,
      },
      cognitive: true,
      documents: {
        deleteDocument: true,
      },
      caregivers: true,
      scores: true,
      alignmentDownload: true,
      alignmentData: true,
      athena: true,
      security: true,
      portalCaregivers: true,
      patientPortalAccess: true,
      appointments: true,
      secureMessaging: true,
    },
  },
  documentation: {
    enrollment: {
      [moduleTypes.Onboarding.id]: true,
      [moduleTypes.SelfServeIntakeConfirmation.id]: true,
      [moduleTypes.SelfServeIntakeLifestyle.id]: true,
      [moduleTypes.SelfServeIntakeMedical.id]: true,
      [moduleTypes.GUIDEIntake.id]: true,
      [moduleTypes.CognitiveCareIntake.id]: true,
    },
    clinical: {
      [moduleTypes.Hypoglycemia.id]: true,
      [moduleTypes.Hyperglycemia.id]: true,
      [moduleTypes.MonthlyCall.id]: true,
      [moduleTypes.QuickNote.id]: true,
      [moduleTypes.LowEngagement.id]: true,
      [moduleTypes.NutritionFollowUp.id]: true,
      [moduleTypes.EducationDelivery.id]: true,
      [moduleTypes.MedicationReconciliation.id]: true,
      [moduleTypes.PhysicianNote.id]: true,
      [moduleTypes.HoldRequest.id]: true,
      [moduleTypes.CloseDiabetesAccount.id]: true,
      [moduleTypes.CloseCognitiveAccount.id]: true,
      [moduleTypes.QuarterlyCheckIn.id]: true,
      [moduleTypes.ClinicalFirstVisitRpmOrder.id]: true,
      [moduleTypes.InitialCoachingCall.id]: true,
      [moduleTypes.MonthlyWellnessVisitAC.id]: true,
      [moduleTypes.CGMAssessment.id]: true,
      [moduleTypes.CGMQualificationExistingMembers.id]: true,
      [moduleTypes.MNTNote.id]: true,
      [moduleTypes.CareCoordination.id]: true,
      [moduleTypes.BiannualVisit.id]: true,
      [moduleTypes.ChartReview.id]: true,
      [moduleTypes.InsulinPumpAssessment.id]: true,
      [moduleTypes.CrisisResponse.id]: true,
    },
    other: {
      [moduleTypes.TechnicalSupport.id]: true,
      [moduleTypes.DeviceWalkthrough.id]: true,
      [moduleTypes.MFADecision.id]: true,
      [moduleTypes.MFARequest.id]: true,
      [moduleTypes.CommercialPlanReview.id]: true,
      [moduleTypes.MFARenewalRequest.id]: true,
    },
    questionnaire: {
      [moduleTypes.PerryHealthAssessment.id]: true,
      [moduleTypes.PerryMotivationQuestionnaire.id]: true,
      [moduleTypes.PHQ9GAD7Assessment.id]: true,
    },
    caseWork: {
      [moduleTypes.CaseWorkAssessment.id]: true,
      [moduleTypes.CaseWorkProgressNote.id]: true,
    },
    cognitive: {
      [moduleTypes.CognitiveClinicalInterview.id]: true,
      [moduleTypes.MemoryImpairmentScreen.id]: true,
      [moduleTypes.ProviderEvaluationOfCognitiveScreening.id]: true,
      [moduleTypes.MoCABlind.id]: true,
      [moduleTypes.NeurologyEvaluation.id]: true,
    },
    guide: {
      [moduleTypes.MindyZBI22Assessment.id]: true,
      [moduleTypes.MindyPRO10Assessment.id]: true,
      [moduleTypes.FAST.id]: true,
      [moduleTypes.CarePlan.id]: true,
      [moduleTypes.GUIDEFirstCall.id]: true,
    },
  },
  note: {
    [InteractionType.Telehealth.id]: [
      InteractionReason.FirstVisit.id,
      InteractionReason.BiAnnualCheckup.id,
      InteractionReason.Psychotherapy.id,
      InteractionReason.RemoteMonitoring.id,
      InteractionReason.OtherEvaluationAndManagement.id,
      InteractionReason.CognitiveEvaluation.id,
      InteractionReason.OtherNonBillable.id,
    ],
    [InteractionType.PhoneCall.id]: [
      InteractionReason.RemoteMonitoring.id,
      InteractionReason.Psychotherapy.id,
      InteractionReason.OtherRpm.id,
      InteractionReason.OtherEvaluationAndManagement.id,
    ],
    [InteractionType.CaseManagement.id]: [InteractionReason.CaseManagement.id],
    [InteractionType.ChartReview.id]: [InteractionReason.ChartReview.id],
    [InteractionType.CareCoordination.id]: [
      InteractionReason.ExternalConsult.id,
      InteractionReason.InternalConsult.id,
      InteractionReason.RecordsRequest.id,
      InteractionReason.EmergencyContactOutreach.id,
      InteractionReason.EmsEscalation.id,
      InteractionReason.OtherNonBillable.id,
    ],
    [InteractionType.Message.id]: [InteractionReason.Message.id],
    [InteractionType.Administrative.id]: [InteractionReason.Administrative.id],
    [InteractionType.Enrollment.id]: [InteractionReason.Intake.id],
    [InteractionType.InPerson.id]: [
      InteractionReason.InitialVisit.id,
      InteractionReason.FollowUpVisit.id,
    ],
    [InteractionType.GUIDE.id]: [
      InteractionReason.NewPatientVisitGUIDE.id,
      InteractionReason.FollowUpGUIDE.id,
      InteractionReason.TransitionalCareManagement.id,
      InteractionReason.OtherPhoneEncounter.id,
    ],
  },
});

export const physicianPermissions = Object.freeze({
  canEditPlatformSettings: true,
  canCreateClient: true,
  canManageClients: true,
  canRequestReports: false,
  canViewChartReviews: true,
  isVisibleInApp: true,
  canManagePromptTemplates: true,
  canManageScheduledMessages: false,
  canManageVisits: false,
  canCreatePatient: false,
  patientChart: {
    quickSettings: {
      canSetAvailableForAlerts: true,
    },
    actions: {
      canAddLabResult: true,
      canCloseAccount: true,
      canAddTask: true,
      canAddDocumentation: true,
      canPlaceAccountOnHold: true,
      canChangeStatusToActive: true,
      canRpmApprove: true,
      canAddSmartGoal: true,
      canAddPrescription: true,
      canReopenAccount: true,
      canRequestMedicationUpdates: true,
      canSendDocumentation: true,
      canSendBulkDocumentation: false,
      canSendLmn: false,
      canAddMedication: true,
      canAddNewIntake: true,
      canMfaApprove: true,
      canClearBGMAlertStatus: true,
      canClearBGMAlerts: true,
      canSendReferral: true,
      canSelectReferredBy: true,
      cognitive: true,
      canSwitchCGMToBGM: false,
    },
    sections: {
      memberInformation: true,
      contacts: true,
      glucoseReadings: true,
      vitalsAndLabResults: {
        editLabResult: true,
        deleteLabResult: true,
      },
      smartGoals: true,
      conversationHistory: true,
      physicianOrders: {
        setRiskLevel: true,
        setAssignedProviders: true,
        assignedAsAC: false,
        assignedAsRD: false,
        assignedAsCDCES: false,
        assignedAsLcsw: false,
        setDiagnosis: true,
        setInsulinUse: true,
        setCGMUse: true,
        setInsulinPump: true,
        setStripOrders: true,
        setTestingOrders: true,
        addAdditionalTestingInfo: true,
      },
      orders: {
        alerts: true,
        labOrders: true,
      },
      medicationsList: true,
      allergyList: true,
      medicalHistory: true,
      familyHistory: true,
      socialHistory: true,
      supplementsList: true,
      encountersList: true,
      surgicalHistory: true,
      scheduledCalls: {
        canEditScheduledCall: true,
        canEditVisitStatus: false,
        canEditVisitAssigned: false,
      },
      careProviders: true,
      documentation: {
        addendAnyNote: true,
        addDocumentation: true,
        requestAdjustments: true,
        downloadDocument: false,
      },
      devicesAndOrders: {
        addBGMDevice: true,
        placeOrder: true,
        setStripCount: true,
        addCGMDevice: true,
        markAsCanceled: true,
        markAsFailed: true,
        deleteBGMDevice: true,
      },
      knownPlans: {
        editBeneficiaryInfo: true,
        addPlan: true,
        editPlan: true,
        changePlanStatus: true,
      },
      payorPriority: {
        editHierarchy: true,
      },
      preAuths: {
        addAuth: true,
        editAuth: true,
      },
      exemptions: {
        addExemption: true,
        editExemption: true,
      },
      cgmOrders: {
        addCGMDevice: true,
        addCGMOrder: true,
        viewCGMOrder: true,
      },
      team: true,
      sdohList: {
        addSdoh: true,
        editSdoh: true,
      },
      tasks: {
        editTask: true,
        addTask: true,
      },
      cognitive: true,
      documents: {
        deleteDocument: false,
      },
      caregivers: true,
      scores: true,
      alignmentDownload: true,
      alignmentData: true,
      athena: false,
    },
  },
  documentation: {
    enrollment: {
      [moduleTypes.Onboarding.id]: true,
      [moduleTypes.SelfServeIntakeConfirmation.id]: true,
      [moduleTypes.SelfServeIntakeLifestyle.id]: true,
      [moduleTypes.SelfServeIntakeMedical.id]: true,
    },
    clinical: {
      [moduleTypes.QuickNote.id]: true,
      [moduleTypes.MedicationReconciliation.id]: true,
      [moduleTypes.PhysicianNote.id]: true,
      [moduleTypes.HoldRequest.id]: true,
      [moduleTypes.CloseDiabetesAccount.id]: true,
      [moduleTypes.CloseCognitiveAccount.id]: true,
      [moduleTypes.QuarterlyCheckIn.id]: true,
      [moduleTypes.ClinicalFirstVisitRpmOrder.id]: true,
      [moduleTypes.CGMAssessment.id]: true,
      [moduleTypes.CGMQualificationExistingMembers.id]: true,
      [moduleTypes.CareCoordination.id]: true,
      [moduleTypes.BiannualVisit.id]: true,
      [moduleTypes.ChartReview.id]: true,
    },
    other: {
      [moduleTypes.TechnicalSupport.id]: true,
      [moduleTypes.DeviceWalkthrough.id]: true,
      [moduleTypes.MFARequest.id]: true,
      [moduleTypes.CommercialPlanReview.id]: true,
      [moduleTypes.MFARenewalRequest.id]: true,
    },
    questionnaire: {
      [moduleTypes.PerryHealthAssessment.id]: true,
      [moduleTypes.PerryMotivationQuestionnaire.id]: true,
      [moduleTypes.PHQ9GAD7Assessment.id]: true,
    },
    caseWork: {},
    cognitive: {
      [moduleTypes.CognitiveClinicalInterview.id]: true,
      [moduleTypes.MemoryImpairmentScreen.id]: true,
      [moduleTypes.ProviderEvaluationOfCognitiveScreening.id]: true,
      [moduleTypes.MoCABlind.id]: true,
      [moduleTypes.NeurologyEvaluation.id]: true,
    },
    guide: {},
  },
  note: {
    [InteractionType.Telehealth.id]: [
      InteractionReason.FirstVisit.id,
      InteractionReason.BiAnnualCheckup.id,
      InteractionReason.Psychotherapy.id,
      InteractionReason.RemoteMonitoring.id,
      InteractionReason.OtherEvaluationAndManagement.id,
      InteractionReason.OtherNonBillable.id,
      InteractionReason.CognitiveEvaluation.id,
    ],
    [InteractionType.CaseManagement.id]: [InteractionReason.CaseManagement.id],
    [InteractionType.ChartReview.id]: [InteractionReason.ChartReview.id],
    [InteractionType.CareCoordination.id]: [
      InteractionReason.ExternalConsult.id,
      InteractionReason.InternalConsult.id,
      InteractionReason.RecordsRequest.id,
      InteractionReason.EmergencyContactOutreach.id,
      InteractionReason.EmsEscalation.id,
      InteractionReason.OtherNonBillable.id,
    ],
    [InteractionType.Message.id]: [InteractionReason.Message.id],
    [InteractionType.Administrative.id]: [InteractionReason.Administrative.id],
    [InteractionType.InPerson.id]: [
      InteractionReason.InitialVisit.id,
      InteractionReason.FollowUpVisit.id,
    ],
    [InteractionType.GUIDE.id]: [
      InteractionReason.NewPatientVisitGUIDE.id,
      InteractionReason.FollowUpGUIDE.id,
      InteractionReason.TransitionalCareManagement.id,
      InteractionReason.OtherPhoneEncounter.id,
    ],
  },
});

export const cdePermissions = Object.freeze({
  canEditPlatformSettings: false,
  canCreateClient: false,
  canManageClients: false,
  canRequestReports: false,
  canViewChartReviews: false,
  isVisibleInApp: true,
  canManagePromptTemplates: false,
  canManageScheduledMessages: false,
  canManageVisits: false,
  canCreatePatient: false,
  patientChart: {
    quickSettings: {
      canSetAvailableForAlerts: true,
    },
    actions: {
      canAddLabResult: true,
      canCloseAccount: false,
      canAddTask: true,
      canAddDocumentation: true,
      canPlaceAccountOnHold: true,
      canChangeStatusToActive: true,
      canRpmApprove: false,
      canAddSmartGoal: true,
      canAddPrescription: false,
      canReopenAccount: false,
      canRequestMedicationUpdates: true,
      canSendDocumentation: false,
      canSendBulkDocumentation: false,
      canSendLmn: false,
      canAddMedication: true,
      canAddNewIntake: false,
      canMfaApprove: false,
      canClearBGMAlertStatus: false,
      canClearBGMAlerts: true,
      canSendReferral: false,
      canSelectReferredBy: false,
      cognitive: true,
      canSwitchCGMToBGM: false,
    },
    sections: {
      memberInformation: true,
      contacts: true,
      glucoseReadings: true,
      vitalsAndLabResults: {
        editLabResult: true,
        deleteLabResult: true,
      },
      smartGoals: true,
      conversationHistory: true,
      physicianOrders: {
        setRiskLevel: true,
        setAssignedProviders: true,
        assignedAsAC: false,
        assignedAsRD: false,
        assignedAsCDCES: true,
        assignedAsLcsw: false,
        setDiagnosis: true,
        setInsulinUse: true,
        setCGMUse: true,
        setInsulinPump: true,
        setStripOrders: true,
        setTestingOrders: true,
        addAdditionalTestingInfo: true,
      },
      orders: {
        alerts: true,
        labOrders: false,
      },
      medicationsList: true,
      allergyList: true,
      medicalHistory: true,
      familyHistory: true,
      socialHistory: true,
      supplementsList: true,
      encountersList: true,
      surgicalHistory: true,
      scheduledCalls: {
        canEditScheduledCall: true,
        canEditVisitStatus: false,
        canEditVisitAssigned: false,
      },
      careProviders: true,
      documentation: {
        addendAnyNote: false,
        addDocumentation: true,
        requestAdjustments: false,
        downloadDocument: false,
      },
      devicesAndOrders: {
        addBGMDevice: true,
        placeOrder: true,
        setStripCount: true,
        addCGMDevice: true,
        markAsCanceled: false,
        markAsFailed: false,
        deleteBGMDevice: false,
      },
      knownPlans: {
        editBeneficiaryInfo: true,
        addPlan: true,
        editPlan: true,
        changePlanStatus: false,
      },
      payorPriority: 'view-only',
      preAuths: 'view-only',
      exemptions: 'view-only',
      cgmOrders: {
        addCGMDevice: true,
        addCGMOrder: false,
        viewCGMOrder: false,
      },
      team: true,
      sdohList: {
        addSdoh: false,
        editSdoh: true,
      },
      tasks: {
        editTask: true,
        addTask: true,
      },
      cognitive: true,
      documents: {
        deleteDocument: false,
      },
      caregivers: false,
      scores: false,
      alignmentDownload: false,
      alignmentData: false,
      athena: false,
    },
  },
  documentation: {
    enrollment: {},
    clinical: {
      [moduleTypes.Hypoglycemia.id]: true,
      [moduleTypes.Hyperglycemia.id]: true,
      [moduleTypes.MonthlyCall.id]: true,
      [moduleTypes.QuickNote.id]: true,
      [moduleTypes.LowEngagement.id]: true,
      [moduleTypes.EducationDelivery.id]: true,
      [moduleTypes.MedicationReconciliation.id]: true,
      [moduleTypes.HoldRequest.id]: true,
      [moduleTypes.QuarterlyCheckIn.id]: true,
      [moduleTypes.CGMAssessment.id]: true,
      [moduleTypes.CareCoordination.id]: true,
      [moduleTypes.ChartReview.id]: true,
      [moduleTypes.InsulinPumpAssessment.id]: true,
    },
    other: {
      [moduleTypes.TechnicalSupport.id]: true,
      [moduleTypes.MFARequest.id]: true,
      [moduleTypes.MFARenewalRequest.id]: true,
    },
    questionnaire: {
      [moduleTypes.PerryHealthAssessment.id]: true,
      [moduleTypes.PerryMotivationQuestionnaire.id]: true,
      [moduleTypes.PHQ9GAD7Assessment.id]: true,
    },
    caseWork: {},
    cognitive: {},
    guide: {},
  },
  note: {
    [InteractionType.PhoneCall.id]: [
      InteractionReason.RemoteMonitoring.id,
      InteractionReason.OtherRpm.id,
    ],
    [InteractionType.CaseManagement.id]: [InteractionReason.CaseManagement.id],
    [InteractionType.ChartReview.id]: [InteractionReason.ChartReview.id],
    [InteractionType.CareCoordination.id]: [
      InteractionReason.ExternalConsult.id,
      InteractionReason.InternalConsult.id,
      InteractionReason.RecordsRequest.id,
      InteractionReason.EmergencyContactOutreach.id,
      InteractionReason.EmsEscalation.id,
      InteractionReason.OtherNonBillable.id,
    ],
    [InteractionType.Message.id]: [InteractionReason.Message.id],
    [InteractionType.Administrative.id]: [InteractionReason.Administrative.id],
  },
});

export const rnPermissions = Object.freeze({
  canEditPlatformSettings: false,
  canCreateClient: false,
  canManageClients: false,
  canRequestReports: false,
  canViewChartReviews: false,
  isVisibleInApp: true,
  canManagePromptTemplates: false,
  canManageScheduledMessages: false,
  canManageVisits: false,
  canCreatePatient: false,
  patientChart: {
    quickSettings: {
      canSetAvailableForAlerts: true,
    },
    actions: {
      canAddLabResult: true,
      canCloseAccount: false,
      canAddTask: true,
      canAddDocumentation: true,
      canPlaceAccountOnHold: true,
      canChangeStatusToActive: true,
      canRpmApprove: false,
      canAddSmartGoal: true,
      canAddPrescription: false,
      canReopenAccount: false,
      canRequestMedicationUpdates: true,
      canSendDocumentation: false,
      canSendBulkDocumentation: false,
      canSendLmn: false,
      canAddMedication: true,
      canAddNewIntake: false,
      canMfaApprove: false,
      canClearBGMAlertStatus: false,
      canClearBGMAlerts: true,
      canSendReferral: false,
      canSelectReferredBy: false,
      cognitive: true,
      canSwitchCGMToBGM: false,
    },
    sections: {
      memberInformation: true,
      contacts: true,
      glucoseReadings: true,
      vitalsAndLabResults: {
        editLabResult: true,
        deleteLabResult: true,
      },
      smartGoals: true,
      conversationHistory: true,
      physicianOrders: {
        setRiskLevel: true,
        setAssignedProviders: true,
        assignedAsAC: false,
        assignedAsRD: false,
        assignedAsCDCES: false,
        assignedAsLcsw: false,
        setDiagnosis: true,
        setInsulinUse: true,
        setCGMUse: true,
        setInsulinPump: true,
        setStripOrders: true,
        setTestingOrders: true,
        addAdditionalTestingInfo: true,
      },
      orders: {
        alerts: true,
        labOrders: false,
      },
      medicationsList: true,
      allergyList: true,
      medicalHistory: true,
      familyHistory: true,
      socialHistory: true,
      supplementsList: true,
      encountersList: true,
      surgicalHistory: true,
      scheduledCalls: {
        canEditScheduledCall: true,
        canEditVisitStatus: false,
        canEditVisitAssigned: false,
      },
      careProviders: true,
      documentation: {
        addendAnyNote: false,
        addDocumentation: true,
        requestAdjustments: false,
        downloadDocument: false,
      },
      devicesAndOrders: {
        addBGMDevice: true,
        placeOrder: true,
        setStripCount: true,
        addCGMDevice: true,
        markAsCanceled: false,
        markAsFailed: false,
        deleteBGMDevice: false,
      },
      knownPlans: {
        editBeneficiaryInfo: true,
        addPlan: true,
        editPlan: true,
        changePlanStatus: false,
      },
      payorPriority: 'view-only',
      preAuths: 'view-only',
      exemptions: 'view-only',
      cgmOrders: {
        addCGMDevice: true,
        addCGMOrder: false,
        viewCGMOrder: false,
      },
      team: true,
      sdohList: {
        addSdoh: false,
        editSdoh: true,
      },
      tasks: {
        editTask: true,
        addTask: true,
      },
      cognitive: true,
      documents: {
        deleteDocument: false,
      },
      caregivers: false,
      scores: false,
      alignmentDownload: false,
      alignmentData: false,
      athena: false,
    },
  },
  documentation: {
    enrollment: {},
    clinical: {
      [moduleTypes.Hypoglycemia.id]: true,
      [moduleTypes.Hyperglycemia.id]: true,
      [moduleTypes.MonthlyCall.id]: true,
      [moduleTypes.QuickNote.id]: true,
      [moduleTypes.LowEngagement.id]: true,
      [moduleTypes.EducationDelivery.id]: true,
      [moduleTypes.MedicationReconciliation.id]: true,
      [moduleTypes.HoldRequest.id]: true,
      [moduleTypes.CGMAssessment.id]: true,
      [moduleTypes.QuarterlyCheckIn.id]: true,
      [moduleTypes.CareCoordination.id]: true,
      [moduleTypes.ChartReview.id]: true,
      [moduleTypes.CrisisResponse.id]: true,
    },
    other: {
      [moduleTypes.TechnicalSupport.id]: true,
      [moduleTypes.MFARequest.id]: true,
      [moduleTypes.MFARenewalRequest.id]: true,
    },
    questionnaire: {
      [moduleTypes.PerryHealthAssessment.id]: true,
      [moduleTypes.PerryMotivationQuestionnaire.id]: true,
      [moduleTypes.PHQ9GAD7Assessment.id]: true,
    },
    caseWork: {
      [moduleTypes.CaseWorkAssessment.id]: true,
      [moduleTypes.CaseWorkProgressNote.id]: true,
    },
    cognitive: {},
    guide: {},
  },
  note: {
    [InteractionType.PhoneCall.id]: [
      InteractionReason.RemoteMonitoring.id,
      InteractionReason.OtherRpm.id,
    ],
    [InteractionType.CaseManagement.id]: [InteractionReason.CaseManagement.id],
    [InteractionType.ChartReview.id]: [InteractionReason.ChartReview.id],
    [InteractionType.CareCoordination.id]: [
      InteractionReason.ExternalConsult.id,
      InteractionReason.InternalConsult.id,
      InteractionReason.RecordsRequest.id,
      InteractionReason.EmergencyContactOutreach.id,
      InteractionReason.EmsEscalation.id,
      InteractionReason.OtherNonBillable.id,
    ],
    [InteractionType.Message.id]: [InteractionReason.Message.id],
    [InteractionType.Administrative.id]: [InteractionReason.Administrative.id],
  },
});

export const accountabilityCoachPermissions = Object.freeze({
  canEditPlatformSettings: false,
  canCreateClient: false,
  canManageClients: false,
  canRequestReports: false,
  canViewChartReviews: false,
  isVisibleInApp: true,
  canManagePromptTemplates: false,
  canManageScheduledMessages: false,
  canManageVisits: false,
  canCreatePatient: false,
  patientChart: {
    quickSettings: {},
    actions: {
      canAddLabResult: true,
      canCloseAccount: false,
      canAddTask: true,
      canAddDocumentation: true,
      canPlaceAccountOnHold: true,
      canChangeStatusToActive: true,
      canRpmApprove: false,
      canAddSmartGoal: true,
      canAddPrescription: false,
      canReopenAccount: false,
      canRequestMedicationUpdates: true,
      canSendDocumentation: false,
      canSendBulkDocumentation: false,
      canSendLmn: false,
      canAddMedication: true,
      canAddNewIntake: false,
      canMfaApprove: false,
      canClearBGMAlertStatus: false,
      canClearBGMAlerts: false,
      canSendReferral: false,
      canSelectReferredBy: false,
      cognitive: true,
      canSwitchCGMToBGM: false,
    },
    sections: {
      memberInformation: true,
      contacts: true,
      glucoseReadings: true,
      vitalsAndLabResults: {
        editLabResult: true,
        deleteLabResult: false,
      },
      smartGoals: true,
      conversationHistory: true,
      physicianOrders: {
        setRiskLevel: true,
        setAssignedProviders: true,
        assignedAsAC: true,
        assignedAsRD: false,
        assignedAsCDCES: false,
        assignedAsLcsw: false,
        setDiagnosis: false,
        setInsulinUse: true,
        setCGMUse: true,
        setInsulinPump: true,
        setStripOrders: true,
        setTestingOrders: true,
        addAdditionalTestingInfo: true,
      },
      orders: {
        alerts: 'view-only',
        labOrders: false,
      },
      medicationsList: true,
      allergyList: true,
      medicalHistory: true,
      familyHistory: true,
      socialHistory: true,
      supplementsList: true,
      encountersList: true,
      surgicalHistory: true,
      scheduledCalls: {
        canEditScheduledCall: true,
        canEditVisitStatus: false,
        canEditVisitAssigned: false,
      },
      careProviders: true,
      documentation: {
        addendAnyNote: false,
        addDocumentation: true,
        requestAdjustments: false,
        downloadDocument: false,
      },
      devicesAndOrders: {
        addBGMDevice: true,
        placeOrder: true,
        setStripCount: true,
        addCGMDevice: true,
        markAsCanceled: false,
        markAsFailed: false,
        deleteBGMDevice: false,
      },
      knownPlans: {
        editBeneficiaryInfo: true,
        addPlan: true,
        editPlan: true,
        changePlanStatus: false,
      },
      payorPriority: 'view-only',
      preAuths: 'view-only',
      exemptions: 'view-only',
      cgmOrders: {
        addCGMDevice: true,
        addCGMOrder: false,
        viewCGMOrder: false,
      },
      team: true,
      sdohList: {
        addSdoh: false,
        editSdoh: true,
      },
      tasks: {
        editTask: true,
        addTask: true,
      },
      cognitive: true,
      documents: {
        deleteDocument: false,
      },
      caregivers: false,
      scores: false,
      alignmentDownload: false,
      alignmentData: false,
      athena: false,
    },
  },
  documentation: {
    enrollment: {},
    clinical: {
      [moduleTypes.QuickNote.id]: true,
      [moduleTypes.LowEngagement.id]: true,
      [moduleTypes.EducationDelivery.id]: true,
      [moduleTypes.HoldRequest.id]: true,
      [moduleTypes.InitialCoachingCall.id]: true,
      [moduleTypes.MonthlyWellnessVisitAC.id]: true,
      [moduleTypes.CGMAssessment.id]: true,
      [moduleTypes.CareCoordination.id]: true,
      [moduleTypes.ChartReview.id]: true,
      [moduleTypes.CrisisResponse.id]: true,
    },
    other: {
      [moduleTypes.TechnicalSupport.id]: true,
      [moduleTypes.DeviceWalkthrough.id]: true,
      [moduleTypes.MFARequest.id]: true,
      [moduleTypes.CommercialPlanReview.id]: true,
      [moduleTypes.MFARenewalRequest.id]: true,
    },
    questionnaire: {
      [moduleTypes.PerryHealthAssessment.id]: true,
      [moduleTypes.PerryMotivationQuestionnaire.id]: true,
      [moduleTypes.PHQ9GAD7Assessment.id]: true,
    },
    caseWork: {},
    cognitive: {
      [moduleTypes.CognitiveClinicalInterview.id]: true,
      [moduleTypes.MemoryImpairmentScreen.id]: true,
      [moduleTypes.ProviderEvaluationOfCognitiveScreening.id]: true,
      [moduleTypes.MoCABlind.id]: true,
    },
    guide: {},
  },
  note: {
    [InteractionType.PhoneCall.id]: [
      InteractionReason.RemoteMonitoring.id,
      InteractionReason.OtherRpm.id,
    ],
    [InteractionType.ChartReview.id]: [InteractionReason.ChartReview.id],
    [InteractionType.CareCoordination.id]: [
      InteractionReason.ExternalConsult.id,
      InteractionReason.InternalConsult.id,
      InteractionReason.RecordsRequest.id,
      InteractionReason.EmergencyContactOutreach.id,
      InteractionReason.EmsEscalation.id,
      InteractionReason.OtherNonBillable.id,
    ],
    [InteractionType.Message.id]: [InteractionReason.Message.id],
    [InteractionType.Administrative.id]: [InteractionReason.Administrative.id],
  },
});

export const externalPhysicianPermissions = Object.freeze({
  canEditPlatformSettings: false,
  canCreateClient: false,
  canManageClients: false,
  canRequestReports: false,
  canViewChartReviews: true,
  isVisibleInApp: false,
  canManagePromptTemplates: false,
  canManageScheduledMessages: false,
  canManageVisits: false,
  canCreatePatient: false,
  patientChart: {
    quickSettings: {},
    actions: {
      canAddLabResult: true,
      canCloseAccount: false,
      canAddTask: true,
      canAddDocumentation: false,
      canPlaceAccountOnHold: true,
      canChangeStatusToActive: false,
      canRpmApprove: false,
      canAddSmartGoal: true,
      canAddPrescription: false,
      canReopenAccount: false,
      canRequestMedicationUpdates: false,
      canSendDocumentation: false,
      canSendBulkDocumentation: false,
      canSendLmn: false,
      canAddMedication: false,
      canAddNewIntake: false,
      canMfaApprove: false,
      canClearBGMAlertStatus: false,
      canClearBGMAlerts: false,
      canSendReferral: false,
      canSelectReferredBy: false,
      cognitive: false,
      canSwitchCGMToBGM: false,
    },
    sections: {
      memberInformation: true,
      contacts: true,
      glucoseReadings: true,
      vitalsAndLabResults: 'view-only',
      smartGoals: 'view-only',
      conversationHistory: true,
      physicianOrders: 'view-only',
      orders: {
        alerts: 'view-only',
        labOrders: false,
      },
      medicationsList: 'view-only',
      allergyList: 'view-only',
      medicalHistory: 'view-only',
      familyHistory: 'view-only',
      socialHistory: 'view-only',
      supplementsList: 'view-only',
      encountersList: 'view-only',
      surgicalHistory: 'view-only',
      scheduledCalls: {
        canEditScheduledCall: true,
        canEditVisitStatus: false,
        canEditVisitAssigned: false,
      },
      careProviders: true,
      documentation: {
        addendAnyNote: false,
        addDocumentation: false,
        requestAdjustments: false,
        downloadDocument: false,
      },
      devicesAndOrders: 'view-only',
      knownPlans: 'view-only',
      payorPriority: 'view-only',
      preAuths: 'view-only',
      exemptions: 'view-only',
      cgmOrders: 'view-only',
      team: false,
      sdohList: {
        addSdoh: false,
        editSdoh: false,
      },
      tasks: {
        editTask: true,
        addTask: true,
      },
      cognitive: true,
      documents: {
        deleteDocument: false,
      },
      caregivers: false,
      scores: false,
      alignmentDownload: false,
      alignmentData: false,
      athena: false,
    },
  },
  note: {},
});

export const viewerPermissions = Object.freeze({
  canEditPlatformSettings: false,
  canCreateClient: false,
  canManageClients: false,
  canRequestReports: false,
  canViewChartReviews: false,
  isVisibleInApp: false,
  canManagePromptTemplates: false,
  canManageScheduledMessages: false,
  canManageVisits: false,
  canCreatePatient: false,
  patientChart: {
    quickSettings: {},
    actions: {
      canAddLabResult: true,
      canCloseAccount: false,
      canAddTask: true,
      canAddDocumentation: false,
      canPlaceAccountOnHold: true,
      canChangeStatusToActive: false,
      canRpmApprove: false,
      canAddSmartGoal: true,
      canAddPrescription: false,
      canReopenAccount: false,
      canRequestMedicationUpdates: false,
      canSendDocumentation: false,
      canSendBulkDocumentation: false,
      canSendLmn: false,
      canAddMedication: false,
      canAddNewIntake: false,
      canMfaApprove: false,
      canClearBGMAlertStatus: false,
      canClearBGMAlerts: false,
      canSendReferral: false,
      canSelectReferredBy: false,
      cognitive: false,
      canSwitchCGMToBGM: false,
    },
    sections: {
      memberInformation: true,
      contacts: true,
      glucoseReadings: true,
      vitalsAndLabResults: 'view-only',
      smartGoals: 'view-only',
      conversationHistory: true,
      physicianOrders: 'view-only',
      orders: {
        alerts: 'view-only',
        labOrders: false,
      },
      medicationsList: 'view-only',
      allergyList: 'view-only',
      medicalHistory: 'view-only',
      familyHistory: 'view-only',
      socialHistory: 'view-only',
      supplementsList: 'view-only',
      encountersList: 'view-only',
      surgicalHistory: 'view-only',
      scheduledCalls: {
        canEditScheduledCall: true,
        canEditVisitStatus: false,
        canEditVisitAssigned: false,
      },
      careProviders: true,
      documentation: {
        addendAnyNote: false,
        addDocumentation: false,
        requestAdjustments: false,
        downloadDocument: false,
      },
      devicesAndOrders: 'view-only',
      knownPlans: 'view-only',
      payorPriority: 'view-only',
      preAuths: 'view-only',
      exemptions: 'view-only',
      cgmOrders: 'view-only',
      team: false,
      sdohList: {
        addSdoh: false,
        editSdoh: false,
      },
      tasks: {
        editTask: true,
        addTask: true,
      },
      cognitive: true,
      documents: {
        deleteDocument: false,
      },
      caregivers: false,
      scores: false,
      alignmentDownload: false,
      alignmentData: false,
      athena: false,
    },
  },
  note: {},
});

export const enrollmentPermissions = Object.freeze({
  canEditPlatformSettings: false,
  canCreateClient: false,
  canManageClients: false,
  canRequestReports: false,
  canViewChartReviews: false,
  isVisibleInApp: true,
  canManagePromptTemplates: false,
  canManageScheduledMessages: false,
  canManageVisits: false,
  canCreatePatient: false,
  patientChart: {
    quickSettings: {},
    actions: {
      canAddLabResult: true,
      canCloseAccount: false,
      canAddTask: true,
      canAddDocumentation: true,
      canPlaceAccountOnHold: false,
      canChangeStatusToActive: false,
      canRpmApprove: false,
      canAddSmartGoal: false,
      canAddPrescription: false,
      canReopenAccount: false,
      canRequestMedicationUpdates: false,
      canSendDocumentation: false,
      canSendBulkDocumentation: false,
      canSendLmn: false,
      canAddMedication: true,
      canAddNewIntake: true,
      canMfaApprove: false,
      canClearBGMAlertStatus: false,
      canClearBGMAlerts: false,
      canSendReferral: false,
      canSelectReferredBy: false,
      cognitive: true,
      canSwitchCGMToBGM: false,
    },
    sections: {
      memberInformation: true,
      contacts: true,
      glucoseReadings: true,
      vitalsAndLabResults: {
        editLabResult: true,
        deleteLabResult: false,
      },
      smartGoals: 'view-only',
      conversationHistory: true,
      physicianOrders: 'view-only',
      orders: {
        alerts: false,
        labOrders: false,
      },
      medicationsList: true,
      allergyList: true,
      medicalHistory: true,
      familyHistory: true,
      socialHistory: 'view-only',
      supplementsList: true,
      encountersList: 'view-only',
      surgicalHistory: true,
      scheduledCalls: {
        canEditScheduledCall: true,
        canEditVisitStatus: false,
        canEditVisitAssigned: false,
      },
      careProviders: true,
      documentation: {
        addendAnyNote: false,
        addDocumentation: true,
        requestAdjustments: false,
        downloadDocument: false,
      },
      devicesAndOrders: 'view-only',
      knownPlans: {
        editBeneficiaryInfo: true,
        addPlan: true,
        editPlan: true,
        changePlanStatus: true,
      },
      payorPriority: {
        editHierarchy: true,
      },
      preAuths: 'view-only',
      exemptions: 'view-only',
      cgmOrders: {
        addCGMDevice: true,
        addCGMOrder: false,
        viewCGMOrder: false,
      },
      team: 'view-only',
      sdohList: {
        addSdoh: false,
        editSdoh: false,
      },
      tasks: {
        editTask: true,
        addTask: true,
      },
      cognitive: true,
      documents: {
        deleteDocument: false,
      },
      caregivers: true,
      scores: false,
      alignmentDownload: false,
      alignmentData: false,
      athena: false,
      security: true,
      portalCaregivers: true,
      patientPortalAccess: true,
      appointments: true,
      secureMessaging: true,
    },
  },
  documentation: {
    enrollment: {
      [moduleTypes.Onboarding.id]: true,
      [moduleTypes.SelfServeIntakeConfirmation.id]: true,
      [moduleTypes.SelfServeIntakeLifestyle.id]: true,
      [moduleTypes.SelfServeIntakeMedical.id]: true,
      [moduleTypes.GUIDEIntake.id]: true,
      [moduleTypes.CognitiveCareIntake.id]: true,
    },
    clinical: {
      [moduleTypes.QuickNote.id]: true,
    },
    other: {
      [moduleTypes.MFARequest.id]: true,
      [moduleTypes.CommercialPlanReview.id]: true,
    },
    questionnaire: {},
    caseWork: {},
    cognitive: {},
    guide: {
      [moduleTypes.MindyZBI22Assessment.id]: true,
      [moduleTypes.MindyPRO10Assessment.id]: true,
      [moduleTypes.FAST.id]: true,
      [moduleTypes.CarePlan.id]: true,
      [moduleTypes.GUIDEFirstCall.id]: true,
    },
  },
  note: {
    [InteractionType.Enrollment.id]: [InteractionReason.Intake.id],
    [InteractionType.GUIDE.id]: [
      InteractionReason.NewPatientVisitGUIDE.id,
      InteractionReason.FollowUpGUIDE.id,
      InteractionReason.TransitionalCareManagement.id,
      InteractionReason.OtherPhoneEncounter.id,
    ],
  },
});

export const mxPermissions = Object.freeze({
  canEditPlatformSettings: false,
  canCreateClient: false,
  canManageClients: false,
  canRequestReports: false,
  canViewChartReviews: false,
  isVisibleInApp: true,
  canManagePromptTemplates: false,
  canManageScheduledMessages: false,
  canManageVisits: false,
  canCreatePatient: false,
  canManageLeads: true,
  patientChart: {
    quickSettings: {},
    actions: {
      canAddLabResult: true,
      canCloseAccount: false,
      canAddTask: true,
      canAddDocumentation: true,
      canPlaceAccountOnHold: true,
      canChangeStatusToActive: true,
      canRpmApprove: false,
      canAddSmartGoal: false,
      canAddPrescription: false,
      canReopenAccount: false,
      canRequestMedicationUpdates: false,
      canSendDocumentation: true,
      canSendBulkDocumentation: false,
      canSendLmn: false,
      canAddMedication: true,
      canAddNewIntake: false,
      canMfaApprove: false,
      canClearBGMAlertStatus: false,
      canClearBGMAlerts: false,
      canSendReferral: true,
      canSelectReferredBy: true,
      cognitive: true,
      canSwitchCGMToBGM: true,
    },
    sections: {
      memberInformation: true,
      contacts: true,
      glucoseReadings: true,
      vitalsAndLabResults: {
        editLabResult: true,
        deleteLabResult: false,
      },
      smartGoals: 'view-only',
      conversationHistory: true,
      physicianOrders: 'view-only',
      orders: {
        alerts: false,
        labOrders: false,
      },
      medicationsList: true,
      allergyList: true,
      medicalHistory: true,
      familyHistory: true,
      socialHistory: 'view-only',
      supplementsList: true,
      encountersList: 'view-only',
      surgicalHistory: true,
      scheduledCalls: {
        canEditScheduledCall: true,
        canEditVisitStatus: false,
        canEditVisitAssigned: false,
      },
      careProviders: true,
      documentation: {
        addendAnyNote: false,
        addDocumentation: true,
        requestAdjustments: false,
        downloadDocument: true,
      },
      devicesAndOrders: {
        addBGMDevice: true,
        placeOrder: true,
        setStripCount: true,
        addCGMDevice: true,
        markAsCanceled: false,
        markAsFailed: false,
        deleteBGMDevice: true,
      },
      knownPlans: {
        editBeneficiaryInfo: true,
        addPlan: true,
        editPlan: true,
        changePlanStatus: false,
      },
      payorPriority: 'view-only',
      preAuths: 'view-only',
      exemptions: 'view-only',
      cgmOrders: {
        addCGMDevice: true,
        addCGMOrder: true,
        viewCGMOrder: true,
      },
      team: 'view-only',
      sdohList: {
        addSdoh: false,
        editSdoh: false,
      },
      tasks: {
        editTask: true,
        addTask: true,
      },
      cognitive: true,
      documents: {
        deleteDocument: false,
      },
      caregivers: true,
      scores: false,
      alignmentDownload: false,
      alignmentData: false,
      athena: false,
      security: true,
      portalCaregivers: true,
      patientPortalAccess: true,
      appointments: true,
      secureMessaging: true,
    },
  },
  documentation: {
    enrollment: {
      [moduleTypes.Onboarding.id]: true,
      [moduleTypes.SelfServeIntakeConfirmation.id]: true,
      [moduleTypes.SelfServeIntakeLifestyle.id]: true,
      [moduleTypes.SelfServeIntakeMedical.id]: true,
      [moduleTypes.GUIDEIntake.id]: true,
    },
    clinical: {
      [moduleTypes.QuickNote.id]: true,
      [moduleTypes.LowEngagement.id]: true,
      [moduleTypes.HoldRequest.id]: true,
    },
    other: {
      [moduleTypes.TechnicalSupport.id]: true,
      [moduleTypes.DeviceWalkthrough.id]: true,
      [moduleTypes.MFARequest.id]: true,
      [moduleTypes.CommercialPlanReview.id]: true,
      [moduleTypes.MFARenewalRequest.id]: true,
    },
    questionnaire: {
      [moduleTypes.PerryHealthAssessment.id]: true,
      [moduleTypes.PerryMotivationQuestionnaire.id]: true,
      [moduleTypes.PHQ9GAD7Assessment.id]: true,
    },
    caseWork: {},
    cognitive: {},
    guide: {
      [moduleTypes.MindyZBI22Assessment.id]: true,
      [moduleTypes.MindyPRO10Assessment.id]: true,
      [moduleTypes.FAST.id]: true,
      [moduleTypes.CarePlan.id]: true,
      [moduleTypes.GUIDEFirstCall.id]: true,
    },
  },
  note: {
    [InteractionType.Telehealth.id]: [InteractionReason.OtherNonBillable.id],
    [InteractionType.CaseManagement.id]: [InteractionReason.CaseManagement.id],
    [InteractionType.ChartReview.id]: [InteractionReason.ChartReview.id],
    [InteractionType.CareCoordination.id]: [InteractionReason.OtherNonBillable.id],
    [InteractionType.Message.id]: [InteractionReason.Message.id],
    [InteractionType.Administrative.id]: [InteractionReason.Administrative.id],
    [InteractionType.GUIDE.id]: [
      InteractionReason.NewPatientVisitGUIDE.id,
      InteractionReason.FollowUpGUIDE.id,
      InteractionReason.TransitionalCareManagement.id,
      InteractionReason.OtherPhoneEncounter.id,
    ],
  },
});

export const rdPermissions = Object.freeze({
  canEditPlatformSettings: false,
  canCreateClient: false,
  canManageClients: false,
  canRequestReports: false,
  canViewChartReviews: false,
  isVisibleInApp: true,
  canManagePromptTemplates: false,
  canManageScheduledMessages: false,
  canManageVisits: false,
  canCreatePatient: false,
  patientChart: {
    quickSettings: {
      canSetAvailableForAlerts: true,
    },
    actions: {
      canAddLabResult: true,
      canCloseAccount: false,
      canAddTask: true,
      canAddDocumentation: true,
      canPlaceAccountOnHold: true,
      canChangeStatusToActive: true,
      canRpmApprove: false,
      canAddSmartGoal: true,
      canAddPrescription: false,
      canReopenAccount: false,
      canRequestMedicationUpdates: true,
      canSendDocumentation: false,
      canSendBulkDocumentation: false,
      canSendLmn: false,
      canAddMedication: true,
      canAddNewIntake: false,
      canMfaApprove: false,
      canClearBGMAlertStatus: false,
      canClearBGMAlerts: true,
      canSendReferral: false,
      canSelectReferredBy: false,
      cognitive: true,
      canSwitchCGMToBGM: false,
    },
    sections: {
      memberInformation: true,
      contacts: true,
      glucoseReadings: true,
      vitalsAndLabResults: {
        editLabResult: true,
        deleteLabResult: false,
      },
      smartGoals: true,
      conversationHistory: true,
      physicianOrders: {
        setRiskLevel: true,
        setAssignedProviders: true,
        assignedAsAC: false,
        assignedAsRD: true,
        assignedAsCDCES: false,
        assignedAsLcsw: false,
        setDiagnosis: true,
        setInsulinUse: true,
        setCGMUse: true,
        setInsulinPump: true,
        setStripOrders: true,
        setTestingOrders: true,
        addAdditionalTestingInfo: true,
      },
      orders: {
        alerts: true,
        labOrders: false,
      },
      medicationsList: true,
      allergyList: true,
      medicalHistory: true,
      familyHistory: true,
      socialHistory: true,
      supplementsList: true,
      encountersList: true,
      surgicalHistory: true,
      scheduledCalls: {
        canEditScheduledCall: true,
        canEditVisitStatus: false,
        canEditVisitAssigned: false,
      },
      careProviders: true,
      documentation: {
        addendAnyNote: false,
        addDocumentation: true,
        requestAdjustments: false,
        downloadDocument: false,
      },
      devicesAndOrders: {
        addBGMDevice: true,
        placeOrder: true,
        setStripCount: true,
        addCGMDevice: true,
        markAsCanceled: false,
        markAsFailed: false,
        deleteBGMDevice: false,
      },
      knownPlans: {
        editBeneficiaryInfo: true,
        addPlan: true,
        editPlan: true,
        changePlanStatus: false,
      },
      payorPriority: 'view-only',
      preAuths: 'view-only',
      exemptions: 'view-only',
      cgmOrders: {
        addCGMDevice: true,
        addCGMOrder: false,
        viewCGMOrder: false,
      },
      team: true,
      sdohList: {
        addSdoh: false,
        editSdoh: true,
      },
      tasks: {
        editTask: true,
        addTask: true,
      },
      cognitive: true,
      documents: {
        deleteDocument: false,
      },
      caregivers: false,
      scores: false,
      alignmentDownload: false,
      alignmentData: false,
      athena: false,
    },
  },
  documentation: {
    enrollment: {},
    clinical: {
      [moduleTypes.Hypoglycemia.id]: true,
      [moduleTypes.Hyperglycemia.id]: true,
      [moduleTypes.MonthlyCall.id]: true,
      [moduleTypes.QuickNote.id]: true,
      [moduleTypes.LowEngagement.id]: true,
      [moduleTypes.NutritionFollowUp.id]: true,
      [moduleTypes.EducationDelivery.id]: true,
      [moduleTypes.MedicationReconciliation.id]: true,
      [moduleTypes.HoldRequest.id]: true,
      [moduleTypes.QuarterlyCheckIn.id]: true,
      [moduleTypes.CGMAssessment.id]: true,
      [moduleTypes.MNTNote.id]: true,
      [moduleTypes.CareCoordination.id]: true,
      [moduleTypes.ChartReview.id]: true,
      [moduleTypes.InsulinPumpAssessment.id]: true,
      [moduleTypes.CrisisResponse.id]: true,
    },
    other: {
      [moduleTypes.TechnicalSupport.id]: true,
      [moduleTypes.MFARequest.id]: true,
      [moduleTypes.MFARenewalRequest.id]: true,
    },
    questionnaire: {
      [moduleTypes.PerryHealthAssessment.id]: true,
      [moduleTypes.PerryMotivationQuestionnaire.id]: true,
      [moduleTypes.PHQ9GAD7Assessment.id]: true,
    },
    caseWork: {},
    cognitive: {},
    guide: {},
  },
  note: {
    [InteractionType.PhoneCall.id]: [
      InteractionReason.RemoteMonitoring.id,
      InteractionReason.OtherRpm.id,
    ],
    [InteractionType.CaseManagement.id]: [InteractionReason.CaseManagement.id],
    [InteractionType.ChartReview.id]: [InteractionReason.ChartReview.id],
    [InteractionType.CareCoordination.id]: [
      InteractionReason.ExternalConsult.id,
      InteractionReason.InternalConsult.id,
      InteractionReason.RecordsRequest.id,
      InteractionReason.EmergencyContactOutreach.id,
      InteractionReason.EmsEscalation.id,
      InteractionReason.OtherNonBillable.id,
    ],
    [InteractionType.Message.id]: [InteractionReason.Message.id],
    [InteractionType.Administrative.id]: [InteractionReason.Administrative.id],
  },
});

export const npPermissions = Object.freeze({
  canEditPlatformSettings: false,
  canCreateClient: false,
  canManageClients: false,
  canRequestReports: false,
  canViewChartReviews: false,
  isVisibleInApp: true,
  canManagePromptTemplates: false,
  canManageScheduledMessages: false,
  canManageVisits: false,
  canCreatePatient: false,
  patientChart: {
    quickSettings: {},
    actions: {
      canAddLabResult: true,
      canCloseAccount: false,
      canAddTask: true,
      canAddDocumentation: true,
      canPlaceAccountOnHold: true,
      canChangeStatusToActive: true,
      canRpmApprove: true,
      canAddSmartGoal: true,
      canAddPrescription: true,
      canReopenAccount: false,
      canRequestMedicationUpdates: true,
      canSendDocumentation: false,
      canSendBulkDocumentation: false,
      canSendLmn: false,
      canAddMedication: true,
      canAddNewIntake: false,
      canMfaApprove: false,
      canClearBGMAlertStatus: false,
      canClearBGMAlerts: false,
      canSendReferral: true,
      canSelectReferredBy: false,
      cognitive: true,
      canSwitchCGMToBGM: false,
    },
    sections: {
      memberInformation: true,
      contacts: true,
      glucoseReadings: true,
      vitalsAndLabResults: {
        editLabResult: true,
        deleteLabResult: true,
      },
      smartGoals: true,
      conversationHistory: true,
      physicianOrders: {
        setRiskLevel: true,
        setAssignedProviders: true,
        assignedAsAC: false,
        assignedAsRD: false,
        assignedAsCDCES: false,
        assignedAsLcsw: false,
        setDiagnosis: true,
        setInsulinUse: true,
        setCGMUse: true,
        setInsulinPump: true,
        setStripOrders: true,
        setTestingOrders: true,
        addAdditionalTestingInfo: true,
      },
      orders: {
        alerts: true,
        labOrders: true,
      },
      medicationsList: true,
      allergyList: true,
      medicalHistory: true,
      familyHistory: true,
      socialHistory: true,
      supplementsList: true,
      encountersList: true,
      surgicalHistory: true,
      scheduledCalls: {
        canEditScheduledCall: true,
        canEditVisitStatus: false,
        canEditVisitAssigned: false,
      },
      careProviders: true,
      documentation: {
        addendAnyNote: true,
        addDocumentation: true,
        requestAdjustments: false,
        downloadDocument: false,
      },
      devicesAndOrders: {
        addBGMDevice: true,
        placeOrder: true,
        setStripCount: true,
        addCGMDevice: true,
        markAsCanceled: false,
        markAsFailed: false,
        deleteBGMDevice: false,
      },
      knownPlans: {
        editBeneficiaryInfo: true,
        addPlan: true,
        editPlan: true,
        changePlanStatus: false,
      },
      payorPriority: 'view-only',
      preAuths: 'view-only',
      exemptions: 'view-only',
      cgmOrders: {
        addCGMDevice: true,
        addCGMOrder: true,
        viewCGMOrder: true,
      },
      team: true,
      sdohList: {
        addSdoh: false,
        editSdoh: true,
      },
      tasks: {
        editTask: true,
        addTask: true,
      },
      cognitive: true,
      documents: {
        deleteDocument: false,
      },
      caregivers: false,
      scores: false,
      alignmentDownload: false,
      alignmentData: false,
      athena: false,
    },
  },
  documentation: {
    enrollment: {},
    clinical: {
      [moduleTypes.QuickNote.id]: true,
      [moduleTypes.HoldRequest.id]: true,
      [moduleTypes.ClinicalFirstVisitRpmOrder.id]: true,
      [moduleTypes.CGMQualificationExistingMembers.id]: true,
      [moduleTypes.BiannualVisit.id]: true,
      [moduleTypes.EducationDelivery.id]: true,
    },
    other: {
      [moduleTypes.MFARequest.id]: true,
      [moduleTypes.MFARenewalRequest.id]: true,
    },
    questionnaire: {
      [moduleTypes.PerryHealthAssessment.id]: true,
      [moduleTypes.PerryMotivationQuestionnaire.id]: true,
      [moduleTypes.PHQ9GAD7Assessment.id]: true,
    },
    caseWork: {},
    cognitive: {},
    guide: {},
  },
  note: {
    [InteractionType.Telehealth.id]: [
      InteractionReason.FirstVisit.id,
      InteractionReason.BiAnnualCheckup.id,
      InteractionReason.Psychotherapy.id,
      InteractionReason.RemoteMonitoring.id,
      InteractionReason.OtherEvaluationAndManagement.id,
      InteractionReason.OtherNonBillable.id,
    ],
    [InteractionType.CaseManagement.id]: [InteractionReason.CaseManagement.id],
    [InteractionType.ChartReview.id]: [InteractionReason.ChartReview.id],
    [InteractionType.CareCoordination.id]: [
      InteractionReason.ExternalConsult.id,
      InteractionReason.InternalConsult.id,
      InteractionReason.RecordsRequest.id,
      InteractionReason.EmergencyContactOutreach.id,
      InteractionReason.EmsEscalation.id,
      InteractionReason.OtherNonBillable.id,
    ],
    [InteractionType.Message.id]: [InteractionReason.Message.id],
    [InteractionType.Administrative.id]: [InteractionReason.Administrative.id],
  },
});

export const rcmPermissions = Object.freeze({
  canEditPlatformSettings: false,
  canCreateClient: false,
  canManageClients: false,
  canRequestReports: true,
  canViewChartReviews: false,
  canRequestGuideReports: true,
  isVisibleInApp: true,
  canManagePromptTemplates: false,
  canManageScheduledMessages: false,
  canManageVisits: false,
  canCreatePatient: false,
  patientChart: {
    quickSettings: {},
    actions: {
      canAddLabResult: false,
      canCloseAccount: false,
      canAddTask: true,
      canAddDocumentation: true,
      canPlaceAccountOnHold: false,
      canChangeStatusToActive: false,
      canRpmApprove: false,
      canAddSmartGoal: false,
      canAddPrescription: false,
      canReopenAccount: false,
      canRequestMedicationUpdates: false,
      canSendDocumentation: true,
      canSendBulkDocumentation: true,
      canSendLmn: true,
      canAddMedication: false,
      canAddNewIntake: false,
      canMfaApprove: false,
      canClearBGMAlertStatus: false,
      canClearBGMAlerts: false,
      canSendReferral: false,
      canSelectReferredBy: false,
      cognitive: false,
      canSwitchCGMToBGM: false,
    },
    sections: {
      memberInformation: true,
      contacts: true,
      glucoseReadings: true,
      vitalsAndLabResults: 'view-only',
      smartGoals: 'view-only',
      conversationHistory: true,
      physicianOrders: 'view-only',
      orders: {
        alerts: false,
        labOrders: false,
      },
      medicationsList: 'view-only',
      allergyList: 'view-only',
      medicalHistory: 'view-only',
      familyHistory: 'view-only',
      socialHistory: 'view-only',
      supplementsList: 'view-only',
      encountersList: 'view-only',
      surgicalHistory: 'view-only',
      scheduledCalls: {
        canEditScheduledCall: true,
        canEditVisitStatus: false,
        canEditVisitAssigned: false,
      },
      careProviders: true,
      documentation: {
        addendAnyNote: false,
        addDocumentation: true,
        requestAdjustments: false,
        downloadDocument: false,
      },
      devicesAndOrders: 'view-only',
      knownPlans: {
        editBeneficiaryInfo: true,
        addPlan: true,
        editPlan: true,
        changePlanStatus: true,
      },
      payorPriority: {
        editHierarchy: true,
      },
      preAuths: {
        addAuth: true,
        editAuth: true,
      },
      exemptions: {
        addExemption: true,
        editExemption: true,
      },
      cgmOrders: 'view-only',
      team: 'view-only',
      sdohList: {
        addSdoh: false,
        editSdoh: false,
      },
      tasks: {
        editTask: true,
        addTask: true,
      },
      cognitive: true,
      documents: {
        deleteDocument: false,
      },
      caregivers: true,
      scores: true,
      alignmentDownload: true,
      alignmentData: true,
      athena: false,
    },
  },
  documentation: {
    enrollment: {},
    clinical: {
      [moduleTypes.QuickNote.id]: true,
    },
    other: {
      [moduleTypes.MFARequest.id]: true,
      [moduleTypes.CommercialPlanReview.id]: true,
    },
    questionnaire: {},
    caseWork: {},
    cognitive: {},
    guide: {},
  },
  note: {
    [InteractionType.CaseManagement.id]: [InteractionReason.CaseManagement.id],
    [InteractionType.ChartReview.id]: [InteractionReason.ChartReview.id],
    [InteractionType.Message.id]: [InteractionReason.Message.id],
    [InteractionType.Administrative.id]: [InteractionReason.Administrative.id],
  },
});

export const systemPermissions = Object.freeze({
  canEditPlatformSettings: false,
  canCreateClient: false,
  canManageClients: false,
  canRequestReports: false,
  canViewChartReviews: false,
  isVisibleInApp: false,
  promptTemplates: {},
  canManagePromptTemplates: false,
  canManageScheduledMessages: false,
  canManageVisits: false,
  canCreatePatient: false,
  patientChart: {
    quickSettings: {},
    actions: {},
    sections: {},
  },
  documentation: {
    enrollment: {},
    clinical: {},
    other: {},
    questionnaire: {},
    caseWork: {},
    cognitive: {},
  },
  note: {},
});

export const socialWorkerPermissions = Object.freeze({
  canEditPlatformSettings: false,
  canCreateClient: false,
  canManageClients: false,
  canRequestReports: false,
  canViewChartReviews: false,
  isVisibleInApp: true,
  canManagePromptTemplates: false,
  canManageScheduledMessages: false,
  canManageVisits: false,
  canCreatePatient: false,
  patientChart: {
    quickSettings: {},
    actions: {
      canAddLabResult: true,
      canCloseAccount: false,
      canAddTask: true,
      canAddDocumentation: true,
      canPlaceAccountOnHold: true,
      canChangeStatusToActive: false,
      canRpmApprove: false,
      canAddSmartGoal: true,
      canAddPrescription: true,
      canReopenAccount: false,
      canRequestMedicationUpdates: true,
      canSendDocumentation: false,
      canSendBulkDocumentation: false,
      canSendLmn: false,
      canAddMedication: true,
      canAddNewIntake: false,
      canMfaApprove: false,
      canClearBGMAlertStatus: false,
      canClearBGMAlerts: false,
      canSendReferral: false,
      canSelectReferredBy: false,
      cognitive: false,
      canSwitchCGMToBGM: false,
    },
    sections: {
      memberInformation: true,
      contacts: true,
      glucoseReadings: true,
      vitalsAndLabResults: {
        editLabResult: true,
        deleteLabResult: false,
      },
      smartGoals: true,
      conversationHistory: true,
      physicianOrders: {
        setRiskLevel: false,
        setAssignedProviders: true,
        assignedAsAC: false,
        assignedAsRD: false,
        assignedAsCDCES: false,
        assignedAsLcsw: true,
        setDiagnosis: false,
        setInsulinUse: false,
        setCGMUse: true,
        setInsulinPump: true,
        setStripOrders: true,
        setTestingOrders: true,
        addAdditionalTestingInfo: true,
      },
      orders: {
        alerts: 'view-only',
        labOrders: false,
      },
      medicationsList: true,
      allergyList: true,
      medicalHistory: true,
      familyHistory: true,
      socialHistory: true,
      supplementsList: true,
      encountersList: true,
      surgicalHistory: true,
      scheduledCalls: {
        canEditScheduledCall: true,
        canEditVisitStatus: false,
        canEditVisitAssigned: false,
      },
      careProviders: true,
      documentation: {
        addendAnyNote: false,
        addDocumentation: true,
        requestAdjustments: false,
        downloadDocument: false,
      },
      devicesAndOrders: {
        addBGMDevice: true,
        placeOrder: false,
        setStripCount: true,
        addCGMDevice: true,
        markAsCanceled: false,
        markAsFailed: false,
        deleteBGMDevice: false,
      },
      knownPlans: {
        editBeneficiaryInfo: true,
        addPlan: true,
        editPlan: true,
        changePlanStatus: true,
      },
      payorPriority: {
        editHierarchy: true,
      },
      preAuths: {
        addAuth: true,
        editAuth: true,
      },
      exemptions: {
        addExemption: true,
        editExemption: true,
      },
      cgmOrders: {
        addCGMDevice: true,
        addCGMOrder: false,
        viewCGMOrder: false,
      },
      team: true,
      sdohList: {
        addSdoh: true,
        editSdoh: true,
      },
      tasks: {
        editTask: true,
        addTask: true,
      },
      cognitive: true,
      documents: {
        deleteDocument: false,
      },
      caregivers: false,
      scores: false,
      alignmentDownload: false,
      alignmentData: false,
      athena: false,
    },
  },
  documentation: {
    enrollment: {},
    clinical: {
      [moduleTypes.QuickNote.id]: true,
      [moduleTypes.ChartReview.id]: true,
      [moduleTypes.CareCoordination.id]: true,
    },
    other: {
      [moduleTypes.HoldRequest.id]: true,
      [moduleTypes.MFARequest.id]: true,
      [moduleTypes.TechnicalSupport.id]: true,
      [moduleTypes.MFARenewalRequest.id]: true,
    },
    questionnaire: {
      [moduleTypes.PHQ9GAD7Assessment.id]: true,
    },
    caseWork: {
      [moduleTypes.CaseWorkAssessment.id]: true,
      [moduleTypes.CaseWorkProgressNote.id]: true,
    },
    cognitive: {},
    guide: {},
  },
  note: {
    [InteractionType.PhoneCall.id]: [
      InteractionReason.RemoteMonitoring.id,
      InteractionReason.Psychotherapy.id,
      InteractionReason.OtherRpm.id,
    ],
    [InteractionType.CaseManagement.id]: [InteractionReason.CaseManagement.id],
    [InteractionType.ChartReview.id]: [InteractionReason.ChartReview.id],
    [InteractionType.CareCoordination.id]: [
      InteractionReason.ExternalConsult.id,
      InteractionReason.InternalConsult.id,
      InteractionReason.RecordsRequest.id,
      InteractionReason.EmergencyContactOutreach.id,
      InteractionReason.EmsEscalation.id,
      InteractionReason.OtherNonBillable.id,
    ],
    [InteractionType.Message.id]: [InteractionReason.Message.id],
    [InteractionType.Administrative.id]: [InteractionReason.Administrative.id],
  },
});

export const neurologistPermissions = Object.freeze({
  canEditPlatformSettings: true,
  canCreateClient: true,
  canManageClients: true,
  canRequestReports: false,
  canViewChartReviews: false,
  isVisibleInApp: true,
  canManagePromptTemplates: true,
  canManageScheduledMessages: false,
  canManageVisits: false,
  canCreatePatient: false,
  patientChart: {
    quickSettings: {
      canSetAvailableForAlerts: true,
    },
    actions: {
      canAddLabResult: true,
      canCloseAccount: true,
      canAddTask: true,
      canAddDocumentation: true,
      canPlaceAccountOnHold: true,
      canChangeStatusToActive: true,
      canRpmApprove: true,
      canAddSmartGoal: true,
      canAddPrescription: true,
      canReopenAccount: true,
      canRequestMedicationUpdates: true,
      canSendDocumentation: true,
      canSendBulkDocumentation: false,
      canSendLmn: false,
      canAddMedication: true,
      canAddNewIntake: true,
      canMfaApprove: true,
      canClearBGMAlertStatus: true,
      canClearBGMAlerts: true,
      canSendReferral: true,
      canSelectReferredBy: true,
      cognitive: true,
      canSwitchCGMToBGM: false,
    },
    sections: {
      memberInformation: true,
      contacts: true,
      glucoseReadings: true,
      vitalsAndLabResults: {
        editLabResult: true,
        deleteLabResult: true,
      },
      smartGoals: true,
      conversationHistory: true,
      physicianOrders: {
        setRiskLevel: true,
        setAssignedProviders: true,
        assignedAsAC: false,
        assignedAsRD: false,
        assignedAsCDCES: false,
        assignedAsLcsw: false,
        setDiagnosis: true,
        setInsulinUse: true,
        setCGMUse: true,
        setInsulinPump: true,
        setStripOrders: true,
        setTestingOrders: true,
        addAdditionalTestingInfo: true,
      },
      orders: {
        alerts: true,
        labOrders: true,
      },
      medicationsList: true,
      allergyList: true,
      medicalHistory: true,
      familyHistory: true,
      socialHistory: true,
      supplementsList: true,
      encountersList: true,
      surgicalHistory: true,
      scheduledCalls: {
        canEditScheduledCall: true,
        canEditVisitStatus: false,
        canEditVisitAssigned: false,
      },
      careProviders: true,
      documentation: {
        addendAnyNote: true,
        addDocumentation: true,
        requestAdjustments: true,
        downloadDocument: false,
      },
      devicesAndOrders: {
        addBGMDevice: true,
        placeOrder: true,
        setStripCount: true,
        addCGMDevice: true,
        markAsCanceled: true,
        markAsFailed: true,
        deleteBGMDevice: true,
      },
      knownPlans: {
        editBeneficiaryInfo: true,
        addPlan: true,
        editPlan: true,
        changePlanStatus: true,
      },
      payorPriority: {
        editHierarchy: true,
      },
      preAuths: {
        addAuth: true,
        editAuth: true,
      },
      exemptions: {
        addExemption: true,
        editExemption: true,
      },
      cgmOrders: {
        addCGMDevice: true,
        addCGMOrder: true,
        viewCGMOrder: true,
      },
      team: true,
      sdohList: {
        addSdoh: true,
        editSdoh: true,
      },
      tasks: {
        editTask: true,
        addTask: true,
      },
      cognitive: true,
      documents: {
        deleteDocument: false,
      },
      caregivers: true,
      scores: true,
      alignmentDownload: true,
      alignmentData: true,
      athena: false,
    },
  },
  documentation: {
    clinical: {
      [moduleTypes.QuickNote.id]: true,
      [moduleTypes.CareCoordination.id]: true,
    },
    questionnaire: {
      [moduleTypes.PHQ9GAD7Assessment.id]: true,
    },
    cognitive: {
      [moduleTypes.NeurologyEvaluation.id]: true,
      [moduleTypes.MoCABlind.id]: true,
    },
  },
  note: {
    [InteractionType.Telehealth.id]: [
      InteractionReason.OtherEvaluationAndManagement.id,
      InteractionReason.CognitiveEvaluation.id,
    ],
    [InteractionType.ChartReview.id]: [InteractionReason.ChartReview.id],
    [InteractionType.CareCoordination.id]: [
      InteractionReason.ExternalConsult.id,
      InteractionReason.InternalConsult.id,
      InteractionReason.RecordsRequest.id,
      InteractionReason.EmergencyContactOutreach.id,
      InteractionReason.EmsEscalation.id,
      InteractionReason.OtherNonBillable.id,
    ],
    [InteractionType.Message.id]: [InteractionReason.Message.id],
    [InteractionType.Administrative.id]: [InteractionReason.Administrative.id],
    [InteractionType.InPerson.id]: [
      InteractionReason.InitialVisit.id,
      InteractionReason.FollowUpVisit.id,
    ],
    [InteractionType.GUIDE.id]: [
      InteractionReason.NewPatientVisitGUIDE.id,
      InteractionReason.FollowUpGUIDE.id,
      InteractionReason.TransitionalCareManagement.id,
      InteractionReason.OtherPhoneEncounter.id,
    ],
  },
});

export const careNavigatorPermissions = Object.freeze({
  canEditPlatformSettings: false,
  canCreateClient: false,
  canManageClients: false,
  canRequestReports: false,
  canViewChartReviews: false,
  isVisibleInApp: true,
  canManagePromptTemplates: true,
  canManageScheduledMessages: false,
  canManageVisits: false,
  canCreatePatient: false,
  patientChart: {
    quickSettings: {
      canSetAvailableForAlerts: false,
    },
    actions: {
      canAddLabResult: true,
      canCloseAccount: false,
      canAddTask: true,
      canAddDocumentation: true,
      canPlaceAccountOnHold: true,
      canChangeStatusToActive: true,
      canRpmApprove: false,
      canAddSmartGoal: true,
      canAddPrescription: true,
      canReopenAccount: false,
      canRequestMedicationUpdates: true,
      canSendDocumentation: true,
      canSendBulkDocumentation: false,
      canSendLmn: false,
      canAddMedication: true,
      canAddNewIntake: false,
      canMfaApprove: false,
      canClearBGMAlertStatus: false,
      canClearBGMAlerts: false,
      canSendReferral: false,
      canSelectReferredBy: false,
      cognitive: false,
      canSwitchCGMToBGM: false,
    },
    sections: {
      memberInformation: true,
      contacts: true,
      glucoseReadings: false,
      vitalsAndLabResults: {
        editLabResult: false,
        deleteLabResult: false,
      },
      smartGoals: true,
      conversationHistory: true,
      physicianOrders: {
        setRiskLevel: true,
        setAssignedProviders: true,
        assignedAsAC: false,
        assignedAsRD: false,
        assignedAsCDCES: false,
        assignedAsLcsw: false,
        setDiagnosis: false,
        setInsulinUse: false,
        setCGMUse: false,
        setInsulinPump: false,
        setStripOrders: false,
        setTestingOrders: false,
        addAdditionalTestingInfo: false,
      },
      orders: {
        alerts: false,
        labOrders: false,
      },
      medicationsList: true,
      allergyList: true,
      medicalHistory: true,
      familyHistory: true,
      socialHistory: true,
      supplementsList: true,
      encountersList: true,
      surgicalHistory: true,
      scheduledCalls: {
        canEditScheduledCall: false,
        canEditVisitStatus: false,
        canEditVisitAssigned: false,
      },
      careProviders: true,
      documentation: {
        addendAnyNote: true,
        addDocumentation: true,
        requestAdjustments: true,
        downloadDocument: true,
      },
      devicesAndOrders: {
        addBGMDevice: false,
        placeOrder: false,
        setStripCount: false,
        addCGMDevice: false,
        markAsCanceled: false,
        markAsFailed: false,
        deleteBGMDevice: false,
      },
      knownPlans: {
        editBeneficiaryInfo: true,
        addPlan: true,
        editPlan: true,
        changePlanStatus: false,
      },
      payorPriority: {
        editHierarchy: false,
      },
      preAuths: {
        addAuth: false,
        editAuth: false,
      },
      exemptions: {
        addExemption: false,
        editExemption: false,
      },
      cgmOrders: {
        addCGMDevice: false,
        addCGMOrder: false,
        viewCGMOrder: false,
      },
      team: false,
      sdohList: {
        addSdoh: true,
        editSdoh: true,
      },
      tasks: {
        editTask: true,
        addTask: true,
      },
      documents: {
        deleteDocument: false,
      },
      caregivers: true,
      scores: true,
      alignmentDownload: true,
      alignmentData: true,
      athena: false,
    },
  },
  documentation: {
    enrollment: {
      [moduleTypes.GUIDEIntake.id]: true,
    },
    clinical: {
      [moduleTypes.QuickNote.id]: true,
      [moduleTypes.ChartReview.id]: true,
    },
    other: {},
    questionnaire: {
      [moduleTypes.PHQ9GAD7Assessment.id]: true,
    },
    caseWork: {},
    cognitive: {},
    guide: {
      [moduleTypes.MindyZBI22Assessment.id]: true,
      [moduleTypes.MindyPRO10Assessment.id]: true,
      [moduleTypes.FAST.id]: true,
      [moduleTypes.CarePlan.id]: true,
      [moduleTypes.GUIDEFirstCall.id]: true,
    },
  },
  note: {
    [InteractionType.GUIDE.id]: [
      InteractionReason.NewPatientVisitGUIDE.id,
      InteractionReason.FollowUpGUIDE.id,
      InteractionReason.TransitionalCareManagement.id,
      InteractionReason.OtherPhoneEncounter.id,
    ],
  },
});

export const npNeurologistPermissions = Object.freeze({
  canEditPlatformSettings: true,
  canCreateClient: true,
  canManageClients: true,
  canRequestReports: false,
  canViewChartReviews: false,
  isVisibleInApp: true,
  canManagePromptTemplates: true,
  canManageScheduledMessages: false,
  canManageVisits: false,
  canCreatePatient: false,
  patientChart: {
    quickSettings: {
      canSetAvailableForAlerts: true,
    },
    actions: {
      canAddLabResult: true,
      canCloseAccount: true,
      canAddTask: true,
      canAddDocumentation: true,
      canPlaceAccountOnHold: true,
      canChangeStatusToActive: true,
      canRpmApprove: true,
      canAddSmartGoal: true,
      canAddPrescription: true,
      canReopenAccount: true,
      canRequestMedicationUpdates: true,
      canSendDocumentation: true,
      canSendBulkDocumentation: false,
      canSendLmn: false,
      canAddMedication: true,
      canAddNewIntake: true,
      canMfaApprove: true,
      canClearBGMAlertStatus: true,
      canClearBGMAlerts: true,
      canSendReferral: true,
      canSelectReferredBy: true,
      cognitive: true,
      canSwitchCGMToBGM: false,
    },
    sections: {
      memberInformation: true,
      contacts: true,
      glucoseReadings: true,
      vitalsAndLabResults: {
        editLabResult: true,
        deleteLabResult: true,
      },
      smartGoals: true,
      conversationHistory: true,
      physicianOrders: {
        setRiskLevel: true,
        setAssignedProviders: true,
        assignedAsAC: false,
        assignedAsRD: false,
        assignedAsCDCES: false,
        assignedAsLcsw: false,
        setDiagnosis: true,
        setInsulinUse: true,
        setCGMUse: true,
        setInsulinPump: true,
        setStripOrders: true,
        setTestingOrders: true,
        addAdditionalTestingInfo: true,
      },
      orders: {
        alerts: true,
        labOrders: true,
      },
      medicationsList: true,
      allergyList: true,
      medicalHistory: true,
      familyHistory: true,
      socialHistory: true,
      supplementsList: true,
      encountersList: true,
      surgicalHistory: true,
      scheduledCalls: {
        canEditScheduledCall: true,
        canEditVisitStatus: false,
        canEditVisitAssigned: false,
      },
      careProviders: true,
      documentation: {
        addendAnyNote: true,
        addDocumentation: true,
        requestAdjustments: true,
        downloadDocument: false,
      },
      devicesAndOrders: {
        addBGMDevice: true,
        placeOrder: true,
        setStripCount: true,
        addCGMDevice: true,
        markAsCanceled: true,
        markAsFailed: true,
        deleteBGMDevice: true,
      },
      knownPlans: {
        editBeneficiaryInfo: true,
        addPlan: true,
        editPlan: true,
        changePlanStatus: true,
      },
      payorPriority: {
        editHierarchy: true,
      },
      preAuths: {
        addAuth: true,
        editAuth: true,
      },
      exemptions: {
        addExemption: true,
        editExemption: true,
      },
      cgmOrders: {
        addCGMDevice: true,
        addCGMOrder: true,
        viewCGMOrder: true,
      },
      team: true,
      sdohList: {
        addSdoh: true,
        editSdoh: true,
      },
      cognitive: true,
      tasks: {
        editTask: true,
        addTask: true,
      },
      documents: {
        deleteDocument: false,
      },
      caregivers: true,
      scores: true,
      alignmentDownload: true,
      alignmentData: true,
    },
  },
  documentation: {
    clinical: {
      [moduleTypes.QuickNote.id]: true,
      [moduleTypes.CareCoordination.id]: true,
    },
    questionnaire: {
      [moduleTypes.PHQ9GAD7Assessment.id]: true,
    },
    cognitive: {
      [moduleTypes.NeurologyEvaluation.id]: true,
      [moduleTypes.MoCABlind.id]: true,
    },
  },
  note: {
    [InteractionType.Telehealth.id]: [
      InteractionReason.OtherEvaluationAndManagement.id,
      InteractionReason.CognitiveEvaluation.id,
    ],
    [InteractionType.ChartReview.id]: [InteractionReason.ChartReview.id],
    [InteractionType.CareCoordination.id]: [
      InteractionReason.ExternalConsult.id,
      InteractionReason.InternalConsult.id,
      InteractionReason.RecordsRequest.id,
      InteractionReason.EmergencyContactOutreach.id,
      InteractionReason.EmsEscalation.id,
      InteractionReason.OtherNonBillable.id,
    ],
    [InteractionType.Message.id]: [InteractionReason.Message.id],
    [InteractionType.Administrative.id]: [InteractionReason.Administrative.id],
    [InteractionType.InPerson.id]: [
      InteractionReason.InitialVisit.id,
      InteractionReason.FollowUpVisit.id,
    ],
    [InteractionType.GUIDE.id]: [
      InteractionReason.NewPatientVisitGUIDE.id,
      InteractionReason.FollowUpGUIDE.id,
      InteractionReason.TransitionalCareManagement.id,
      InteractionReason.OtherPhoneEncounter.id,
    ],
  },
});

export const defaultRolePermissions = Object.freeze({
  [roles.Admin.id]: adminPermissions,
  [roles.Viewer.id]: viewerPermissions,
  [roles.MXSpecialist.id]: mxPermissions,
  [roles.RN.id]: rnPermissions,
  [roles.CDE.id]: cdePermissions,
  [roles.Physician.id]: physicianPermissions,
  [roles.ExternalPhysician.id]: externalPhysicianPermissions,
  [roles.Enrollment.id]: enrollmentPermissions,
  [roles.AccountabilityCoach.id]: accountabilityCoachPermissions,
  [roles.RD.id]: rdPermissions,
  [roles.NP.id]: npPermissions,
  [roles.RCM.id]: rcmPermissions,
  [roles.System.id]: systemPermissions,
  [roles.SocialWorker.id]: socialWorkerPermissions,
  [roles.Neurologist.id]: neurologistPermissions,
  [roles.CareNavigator.id]: careNavigatorPermissions,
  [roles.NPNeurologist.id]: npNeurologistPermissions,
});

export const strictRolePermissions = Object.freeze({
  [roles.Admin.id]: { canSetCallDisposition: false },
  [roles.Viewer.id]: { canSetCallDisposition: false },
  [roles.MXSpecialist.id]: { canSetCallDisposition: true },
  [roles.RN.id]: { canSetCallDisposition: true },
  [roles.CDE.id]: { canSetCallDisposition: true },
  [roles.Physician.id]: { canSetCallDisposition: true },
  [roles.ExternalPhysician.id]: { canSetCallDisposition: false },
  [roles.Enrollment.id]: { canSetCallDisposition: false },
  [roles.AccountabilityCoach.id]: { canSetCallDisposition: true },
  [roles.RD.id]: { canSetCallDisposition: true },
  [roles.NP.id]: { canSetCallDisposition: true },
  [roles.RCM.id]: { canSetCallDisposition: false },
  [roles.System.id]: { canSetCallDisposition: false },
  [roles.SocialWorker.id]: { canSetCallDisposition: true },
  [roles.Neurologist.id]: { canSetCallDisposition: false },
  [roles.CareNavigator.id]: { canSetCallDisposition: false },
  [roles.NPNeurologist.id]: { canSetCallDisposition: false },
});
